import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import type { ApiContractMetadata } from '../../models';
import {
  AiApiSpec_addToModelTrainingQueueQueryParamsSchema,
  AiLogSchema,
  AiPredictionLogsWriteSchema,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GetAiPredictionStatusForDateRangeBodySchema,
  MetaForPredictionsInDateRangeSchema,
  OKGeneric,
  PresentableAiPredictionSchema,
  QueryAiPredictionStatusForDateRangeSchema,
  QueryPredictionsInDateRangeSchema,
  RefreshPredictionsInDateRangeBodySchema,
  RejectAiPredictionWriteSchema,
} from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

export const apiAiPredictionsRoute = c.router({
  /**
   * @note Inlined predictions has been moved to separate endpoint, update this payload
   *       After initial deploy (to avoid breaking changes).
   *      Consider inferring `dates_updated_at` from results, though (here or rtkquery?)
   */
  getAiPredictionsInDateRange: {
    method: 'GET',
    path: `/1.0/ai/predictions/time-intervals`,
    summary: 'Predicted intervals of time!',
    description:
      'Predictions from the AI on what project/label a given interval of time should be assigned to.',
    query: QueryPredictionsInDateRangeSchema.partial({ user_id: true }),
    responses: {
      200: z.object({
        ai_predictions: z.array(PresentableAiPredictionSchema),
        meta: MetaForPredictionsInDateRangeSchema,
      }),
    },
  },
  /**
   * Creates a "negative prediction"
   * The negative record will persist when AI predictions are wiped/re-created
   *
   * Negative predictions are used to omit certain time intervals from the predictions we show to end-users
   */
  rejectAiPrediction: {
    method: 'DELETE',
    path: `/1.0/ai/predictions/time-intervals/reject`,
    body: RejectAiPredictionWriteSchema,
    summary: 'Reject/delete (parts of) a time interval prediction',
    description:
      'Creates a "negative time interval", which will omit the given from/to from predicted intervals returned via `GET /predictions/time-intervals`',
    responses: {
      200: OKGeneric,
    },
  },
  getAiPredictionStatusForDateRange: {
    method: 'GET',
    path: `/1.0/ai/predictions/status`,
    summary: 'Get AI prediction logs',
    description: 'Returns the over-all status of AI & predictions',
    query: QueryAiPredictionStatusForDateRangeSchema,
    responses: {
      200: GetAiPredictionStatusForDateRangeBodySchema,
    },
  },
  setAiPredictionStatus: {
    method: 'POST',
    path: `/1.0/ai/prediction_logs`,
    summary: 'Create AI prediction log entry, used to track AI status',
    description: 'Creates a new ai_prediction_logs entry to track AI status',
    body: AiPredictionLogsWriteSchema.omit({
      id: true,
      updated_at: true,
      conflict_key: true,
      user_id: true,
    }),
    responses: {
      200: z.object({
        log: AiLogSchema,
        message: z.string(),
      }),
    },
  },
  /** End-user endpoint for asking AI to refresh time interval predictions */
  refreshAiPredictionsInDateRange: {
    method: 'POST',
    path: `/1.0/ai/predictions/refresh`,
    summary: 'Refresh AI predictions',
    description:
      'Create or refresh predicted time intervals for this YYYY-MM-DD date\n\nAI engine will calculate day range according to the Timely user’s timezone.',
    body: RefreshPredictionsInDateRangeBodySchema,
    responses: {
      200: z.object({
        log: AiLogSchema.nullable(),
        debug: z.object({}).passthrough().optional(),
      }),
    },
    metadata: {
      rateLimitPreset: 'ai_inference',
    } satisfies ApiContractMetadata,
  },
  /** @todo Update `path` to right namespace */
  retrainAiModel: {
    method: 'POST',
    path: `/1.0/ai/preferences/retrain-model`,
    // path: '/ai/predictions/retrain-model',
    summary: 'Retrain AI predictions',
    body: AiApiSpec_addToModelTrainingQueueQueryParamsSchema.pick({
      timely_account_id: true,
      timely_user_id: true,
    }),
    description: 'Retrain the AI model for time interval predictions',
    responses: {
      200: z.object({
        log: AiLogSchema.nullable(),
        debug: z.object({}).passthrough().optional(),
      }),
    },
    metadata: {
      rateLimitPreset: 'ai_model_training',
    } satisfies ApiContractMetadata,
  },
  /** @todo Update `path` to right namespace */
  resetAiModel: {
    method: 'POST',
    path: `/1.0/ai/preferences/reset-model`,
    // path: '/ai/predictions/reset-model',
    summary: 'Reset AI predictions',
    body: z.object({
      timely_account_id: GenericTimelyAccountId,
      timely_user_id: GenericTimelyUserId,
      model_reset_at: z.string().datetime({ offset: true }).optional().openapi({
        description: 'AI will only look at time tracking/logging patterns _after_ this date. ',
      }),
    }),
    description:
      'Right now: Logs "reset request" in db.\n\n\nPossible future: Timestamp + ping to AI will trigger reset + retrain automatically.',
    responses: {
      200: OKGeneric,
    },
  },
});
